import React, { useEffect, useState } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography/Typography';

import TextField, { TextFieldProps } from '@mui/material/TextField/TextField';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@mui/system/styled';
import Button from '@mui/material/Button/Button';
import PublishIcon from '@mui/icons-material/Publish';
import IconButton from '@mui/material/IconButton/IconButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import { observer } from 'mobx-react';
import { TransitionGroup } from 'react-transition-group';
import Collapse from '@mui/material/Collapse/Collapse';
import { createUseStyles } from 'react-jss';
import useMediaQuery from '@mui/system/useMediaQuery/useMediaQuery';
import ViewWrapper from './ViewWrapper';
import lmsIntegrationAgent from '../../../../services/api/lmsIntegrationAgent';
import { IIntegrationItem, IAddLMSLinkRequest, ICourseLMSLinkItem, IPublishLogEntry, IPublishResult, ELMSType } from '../../../../services/models/LMSLinkModel';
import { NiasLogoIcon, WPLogoIcon } from '../../../../features/integrations/ActiveIntegrations';
import moment from 'moment';
import { useStore } from '../../../../store/store';
import { useSnackbar } from 'notistack';
import CircularLoading from '../../../../components/loading/circularLoading';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import Select, { SelectChangeEvent } from '@mui/material/Select/Select';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import InputLabel from '@mui/material/InputLabel/InputLabel';
import FormControl from '@mui/material/FormControl/FormControl';
import LinkedIntegrations from './publish/LinkedIntegrations';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import { usePollingController } from './utils/useCourseLogsPolling';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Divider from '@mui/material/Divider/Divider';
import { DynamicImageField } from '../../../../components/properties/Properties';

interface IPublishCourseViewProps {
  courseID: number;
  ownerID: number;
  loadingOwnerID: boolean;
}

// Interface for publish logs based on actual API response
interface IPublishLogItem {
  id: number;
  upload_date: string;
  permalink: string;
  operation: string;
  publishLog: {
    creatorCourseId: number;
    lmsCustomerId: number;
    lmsCustomerCourseId: number;
    publishDate: string;
    ownerId: number;
    caption: string;
    lmsCustomerUrl: string;
    lmsCourseName: string;
    lmsTypeId: number;
  };
  integrationUrl: string;
  status: string | undefined;
  message?: string;
  success?: boolean;
  body?: string;
}

// Interface for linked integrations that combines WP integration info with course link info
export interface ILinkedIntegration extends IIntegrationItem {
  courseLMSLink?: ICourseLMSLinkItem;
}

// Helper function to convert fetched log entries to display format
const convertLogEntryToDisplayLog = (logEntry: IPublishLogEntry, integrationUrl: string, index: number): IPublishLogItem => {
  //console.log('logEntry', logEntry, integrationUrl);
  return {
    id: Date.now() + index,
    upload_date: logEntry.publishDate,
    permalink: logEntry.lmsCustomerUrl,
    operation: 'published',
    publishLog: logEntry,
    integrationUrl: logEntry.lmsCustomer,
    status: logEntry.caption,
    message: logEntry.caption,
  };
};

// Helper function to convert publish result to display log
const convertPublishResultToDisplayLog = (result: IPublishResult, integrationUrl: string, index: number): IPublishLogItem => {
  //console.log('publishResult', result, integrationUrl, JSON.parse(result.wpBody));

  return {
    id: Date.now() + index,
    upload_date: result.publishLog.publishDate,
    permalink: result.publishLog.lmsCustomerUrl,
    operation: result.success ? 'published' : 'failed',
    publishLog: result.publishLog,
    integrationUrl: integrationUrl,
    status: result.success ? result.publishLog.caption : 'error',
    message: result.success ? result.publishLog.caption : 'Failed to publish',
    success: result.success,
    body: result.wpBody,
  };
};

export default observer(function PublishCourseView({ courseID, ownerID, loadingOwnerID }: IPublishCourseViewProps) {
  const { courseStore, publishStore } = useStore();
  const { courseViewModel, courseProperties } = courseStore;
  const { targetCourseList, getTargetCourseList, updateLMSLink, isTargetCourseListLoaded, getPublishLogs } = publishStore;

  const { enqueueSnackbar } = useSnackbar();
  const [transitionDuration, setTransitionDuration] = useState(0);
  const [publishLogs, setPublishLogs] = useState<IPublishLogItem[]>([]);
  const theme = useTheme();
  const breakpoint = useMediaQuery(theme.breakpoints.down('xl'));
  const [integrations, setIntegrations] = useState<IIntegrationItem[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [linkedIntegrations, setLinkedIntegrations] = useState<ILinkedIntegration[]>([]);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [publishingStates, setPublishingStates] = useState<{ [key: number]: boolean }>({});
  const [linkingIntegration, setLinkingIntegration] = useState<number | null>(null);
  const [_deletingIntegration, setDeletingIntegration] = useState<number | null>(null);

  const [integrationSettings, setIntegrationSettings] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IIntegrationItem>();
  const [targetCourse, setTargetCourse] = useState(0); //This is just the value of the select. The actual target course is stored in IIntegrationItem.
  const { start: startPollingLogs, stop: stopPollingLogs, isRunning, isPaused } = usePollingController(() => getPublishLogsPolling(), 500);

  const [language, setLanguage] = useState('en');

  const handleOpenIntegrationSettings = (aIntegration: IIntegrationItem) => {
    getTargetCourseList(aIntegration.customerId);
    setSelectedIntegration(aIntegration);
    setIntegrationSettings(true);
    if (aIntegration.targetCourse?.id) {
      setTargetCourse(aIntegration.targetCourse.id);
    }
  };

  const handleCloseIntegrationSettings = () => {
    setSelectedIntegration(undefined);
    setIntegrationSettings(false);
    setTargetCourse(0);
  };

  const handleChangeTargetCourse = (aEvent: SelectChangeEvent<number>) => {
    setTargetCourse(Number(aEvent.target.value));
  };

  const getPublishLogsPolling = () => {
    getPublishLogs(courseID).then((aResponse) => {
      let isSuccess = true;
      publishLogs.forEach((aLog) => {
        if (aLog.status === 'success') return;
        const targetTime = new Date(aLog.upload_date);
        const now = new Date();
        const diffMs = Math.abs(now.getTime() - targetTime.getTime());
        const diffMinutes = diffMs / (1000 * 60);
        if (diffMinutes <= 10) {
          //within 10 minutes old log
          const logDate = aLog.upload_date;
          const newLog = aResponse.logs.find((x) => x.publishDate == logDate.replace('Z', ''));
          if (newLog?.caption !== 'success') {
            isSuccess = false;
          }
        }
      });
      if (isSuccess) {
        stopPollingLogs();
        const displayLogs: IPublishLogItem[] = aResponse.logs
          .filter((logEntry) => logEntry.lmsTypeId === ELMSType.WordPress || logEntry.lmsTypeId === ELMSType.Mentorkit_LMS)
          .map((logEntry, index) => {
            const integrationUrl = logEntry.lmsCustomerUrl || 'Unknown Integration';
            return convertLogEntryToDisplayLog(logEntry, integrationUrl, index);
          });

        setPublishLogs(displayLogs);
      }
    });
  };

  const handleUpdateIntegration = () => {
    if (!selectedIntegration) return;
    updateLMSLink(targetCourse, selectedIntegration)
      .then(() => {
        handleCloseIntegrationSettings();
      })
      .catch(() => {
        console.error('something went wrong with updateLMSLink.');
      });
  };

  const handleAddIntegration = async (integration: IIntegrationItem) => {
    /* if (!linkedIntegrations.find((i) => i.customerId === integration.customerId)) { */
    setLinkingIntegration(integration.customerId);
    try {
      const courseName = courseViewModel?.title || `Course ${courseID}`;

      const linkRequest: IAddLMSLinkRequest = {
        lmsCustomerId: integration.customerId,
        creatorCourseId: courseID,
        lmsTypeId: integration.lmsTypeId,
        lmsCourseName: integration.lmsTypeId == 2 ? 'New Course' : courseName,
        identifierLMSCourse: integration.lmsTypeId == 2 ? '0' : courseName,
      };

      const response = await lmsIntegrationAgent.CourseLMSLink.addLMSLink(linkRequest);

      const newLinkedIntegration: ILinkedIntegration = {
        ...integration,
        courseLMSLink: {
          lmsCustomerId: response.lmsCustomerId,
          lmsCustomerCourseId: response.lmsCustomerCourseId,
          creatorCourseId: response.creatorCourseId,
          lmsTypeId: response.lmsTypeId,
          lmsCourseName: response.lmsCourseName,
          identifierLMSCourse: response.identifierLMSCourse,
          publishURL: response?.publishURL || '',
        },
      };
      setLinkedIntegrations([...linkedIntegrations, newLinkedIntegration]);
      enqueueSnackbar(`Integration "${integration.url}" linked successfully!`, {
        variant: 'success',
      });
      //console.log('Integration linked successfully:', integration.url);
    } catch (error) {
      console.error('Error linking integration:', error);
      enqueueSnackbar(`Failed to link integration "${integration.url}". Please try again.`, {
        variant: 'error',
      });
    } finally {
      setLinkingIntegration(null);
    }
    /* } */
    setShowIntegrationsModal(false);
  };

  const handleRemoveIntegration = async (customerId: number) => {
    const integration = linkedIntegrations.find((i) => i.customerId === customerId);
    if (!integration || !integration.courseLMSLink) {
      enqueueSnackbar('Unable to remove integration: missing link information', {
        variant: 'error',
      });
      return;
    }

    setDeletingIntegration(customerId);

    try {
      await lmsIntegrationAgent.CourseLMSLink.deleteCourseLink(integration.courseLMSLink.lmsCustomerId, courseID);
      setLinkedIntegrations(linkedIntegrations.filter((i) => i.customerId !== customerId));

      enqueueSnackbar(`Course link with "${integration.url}" removed successfully!`, {
        variant: 'success',
      });

      //console.log('Integration removed successfully:', integration.url);
    } catch (error) {
      //console.error('Error removing integration:', error);
      enqueueSnackbar(`Failed to course link with "${integration.url}". Please try again.`, {
        variant: 'error',
      });
    } finally {
      setDeletingIntegration(null);
    }
  };

  const handlePublishToIntegration = async (integration: IIntegrationItem) => {
    setPublishingStates((prev) => ({ ...prev, [integration.customerId]: true }));

    let courseImageUrl = '';

    if (courseProperties) {
      const courseImage = courseProperties.settings.items.avatar.option.value;
      const imageBaseURL = process.env.REACT_APP_ENV_BASEURL;
      if (courseImage) courseImageUrl = courseImage.includes('https') ? courseImage : 'https://' + imageBaseURL + '.norskinteraktiv.no/niasAPI' + courseImage;
    }

    try {
      const res = await lmsIntegrationAgent.CourseLMSLink.exportToPublish(courseID, '1.2', language, true, integration.customerId, courseImageUrl);

      // Process all results from the response
      const newLogEntries: IPublishLogItem[] = res.results.map((result, index) => {
        const integrationUrl = result.publishLog.lmsCustomerUrl || integration.url;
        return convertPublishResultToDisplayLog(result, integrationUrl, index);
      });

      setTransitionDuration(300);
      setPublishLogs((prev) => [...newLogEntries, ...prev]);

      // Show success message based on results
      const successCount = res.results.filter((r) => r.success).length;
      const totalCount = res.results.length;

      if (successCount === totalCount) {
        enqueueSnackbar(`Course was pushed to ${totalCount} integration(s), awaiting confirmation!`, {
          variant: 'success',
        });
        startPollingLogs();
      } else if (successCount > 0) {
        enqueueSnackbar(`Course published to ${successCount} of ${totalCount} integrations. Check publish history for details.`, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Failed to publish course to any integrations. Please try again.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error exporting course for integration:', error);

      const errorLogEntry: IPublishLogItem = {
        id: Date.now(),
        permalink: '',
        upload_date: new Date().toString(),
        operation: 'failed',
        publishLog: {
          creatorCourseId: courseID,
          lmsCustomerId: integration.customerId,
          lmsCustomerCourseId: 0,
          publishDate: new Date().toString(),
          ownerId: ownerID || 0,
          caption: 'error',
          lmsCustomerUrl: integration.url,
          lmsCourseName: integration.targetCourse?.name ?? '',
          lmsTypeId: integration.lmsTypeId,
        },
        integrationUrl: integration.url,
        status: 'error',
        message: 'Failed to publish course',
      };

      setPublishLogs((prev) => [errorLogEntry, ...prev]);

      enqueueSnackbar(`Failed to publish course to "${integration.url}". Please try again.`, {
        variant: 'error',
      });
    } finally {
      setPublishingStates((prev) => ({ ...prev, [integration.customerId]: false }));
    }
  };

  const handlePublishToAllIntegrations = async () => {
    if (linkedIntegrations.length === 0) return;

    // Set all integrations to publishing state
    const publishingStateUpdates: { [key: number]: boolean } = {};
    linkedIntegrations.forEach((integration) => {
      publishingStateUpdates[integration.customerId] = true;
    });
    setPublishingStates((prev) => ({ ...prev, ...publishingStateUpdates }));

    let courseImageUrl = '';

    if (courseProperties) {
      const courseImage = courseProperties.settings.items.avatar.option.value;
      const imageBaseURL = process.env.REACT_APP_ENV_BASEURL;
      if (courseImage) courseImageUrl = courseImage.includes('https') ? courseImage : 'https://' + imageBaseURL + '.norskinteraktiv.no/niasAPI' + courseImage;
    }

    try {
      // Call without specific lmsCustomerId to publish to all linked integrations
      const res = await lmsIntegrationAgent.CourseLMSLink.exportToPublish(courseID, '1.2', language, true, undefined, courseImageUrl);

      // Process all results from the response
      const newLogEntries: IPublishLogItem[] = res.results.map((result, index) => {
        const integration = linkedIntegrations.find((int) => int.customerId === result.lmsCustomerId);
        const integrationUrl = result.publishLog.lmsCustomerUrl || integration?.url || 'Unknown Integration';
        return convertPublishResultToDisplayLog(result, integrationUrl, index);
      });

      setTransitionDuration(300);
      setPublishLogs((prev) => [...newLogEntries, ...prev]);

      // Show success message based on results
      const successCount = res.results.filter((r) => r.success).length;
      const totalCount = res.results.length;

      if (successCount === totalCount) {
        enqueueSnackbar(`Course was pushed to all ${totalCount} integration(s), awaiting confirmation!`, {
          variant: 'success',
        });
      } else if (successCount > 0) {
        enqueueSnackbar(`Course published to ${successCount} of ${totalCount} integrations. Check publish history for details.`, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Failed to publish course to any integrations. Please try again.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error exporting course for all integrations:', error);

      // Create error log entries for each linked integration
      const errorLogEntries: IPublishLogItem[] = linkedIntegrations.map((integration, index) => ({
        id: Date.now() + index,
        permalink: '',
        upload_date: new Date().toString(),
        operation: 'failed',
        publishLog: {
          creatorCourseId: courseID,
          lmsCustomerId: integration.customerId,
          lmsCustomerCourseId: integration.courseLMSLink?.lmsCustomerCourseId || 0,
          publishDate: new Date().toString(),
          ownerId: ownerID || 0,
          caption: 'error',
          lmsCustomerUrl: integration.url,
          lmsCourseName: integration.targetCourse?.name ?? '',
          lmsTypeId: integration.lmsTypeId,
        },
        integrationUrl: integration.url,
        status: 'error',
        message: 'Failed to publish course',
      }));

      setPublishLogs((prev) => [...errorLogEntries, ...prev]);

      enqueueSnackbar('Failed to publish course to integrations. Please try again.', {
        variant: 'error',
      });
    } finally {
      // Reset all publishing states
      const publishingStateResets: { [key: number]: boolean } = {};
      linkedIntegrations.forEach((integration) => {
        publishingStateResets[integration.customerId] = false;
      });
      setPublishingStates((prev) => ({ ...prev, ...publishingStateResets }));
    }
  };

  const getStatusColor = (status: IPublishLogItem['status']) => {
    switch (status) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusText = (status: IPublishLogItem['status']) => {
    switch (status) {
      case 'success':
        return 'Published';
      case 'error':
        return 'Failed';
      case 'pending':
        return 'Publishing...';
      default:
        return 'Unknown';
    }
  };

  const useStyles = createUseStyles((theme: Theme) => ({
    publishRow: {
      '&:nth-child(n+1)': {
        borderBottom: '1px solid #515151',
      },
      '&:last-child': { border: 0 },
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.bgOne.main,
      },
    },
    setting: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  }));

  const classes = useStyles({ theme });

  const isSelectableIntegration = (integration: IIntegrationItem) =>
    integration.lmsTypeId === ELMSType.Mentorkit_LMS || (integration.lmsTypeId === ELMSType.WordPress && !linkedIntegrations.some((linked) => linked.customerId === integration.customerId));

  useEffect(() => {
    const fetchIntegrationsLinksAndLogs = async () => {
      if (!ownerID) {
        setLoadingIntegrations(false);
        setLoadingLogs(false);
        setIntegrations([]);
        setLinkedIntegrations([]);
        setPublishLogs([]);
        return;
      }

      try {
        setLoadingIntegrations(true);

        const [rawIntegrationsData, existingLinksData, publishLogsData] = await Promise.all([
          lmsIntegrationAgent.WPLink.getWPLinks(ownerID),
          lmsIntegrationAgent.CourseLMSLink.getLinksByCourse(courseID),
          lmsIntegrationAgent.CourseLMSLink.getPublishLogsByCourse(courseID),
        ]);

        const integrationsData: IIntegrationItem[] = (rawIntegrationsData as any).items.map((item: any) => ({
          customerId: item.lmsCustomerId,
          url: item.lmsCustomer,
          lmsTypeId: item.lmsTypeId,
          ownerId: item.ownerId,
        }));

        const filteredIntegrations = integrationsData.filter((int) => int.lmsTypeId === ELMSType.WordPress || int.lmsTypeId === ELMSType.Mentorkit_LMS);
        setIntegrations(filteredIntegrations);

        const filteredCourseLinks = existingLinksData.items.filter((courseLink) => courseLink.lmsTypeId === ELMSType.WordPress || courseLink.lmsTypeId === ELMSType.Mentorkit_LMS);
        const linkedIntegrationsData: ILinkedIntegration[] = filteredCourseLinks.map((courseLink) => {
          const integration = integrationsData.find((int) => int.customerId === courseLink.lmsCustomerId);
          if (integration) {
            return {
              ...integration,
              courseLMSLink: courseLink,
            };
          }
          return {
            customerId: courseLink.lmsCustomerId,
            url: courseLink.publishURL || '',
            courseLMSLink: courseLink,
            lmsTypeId: courseLink.lmsTypeId,
          };
        });

        setLinkedIntegrations(linkedIntegrationsData);

        const displayLogs: IPublishLogItem[] = publishLogsData.logs
          .filter((logEntry) => logEntry.lmsTypeId === ELMSType.WordPress || logEntry.lmsTypeId === ELMSType.Mentorkit_LMS)
          .map((logEntry, index) => {
            const integrationUrl = logEntry.lmsCustomerUrl || 'Unknown Integration';
            return convertLogEntryToDisplayLog(logEntry, integrationUrl, index);
          });

        setPublishLogs(displayLogs);
      } catch (error) {
        console.error('Error fetching integrations, links, and logs:', error);
        setIntegrations([]);
        setLinkedIntegrations([]);
        setPublishLogs([]);
      } finally {
        setLoadingIntegrations(false);
        setLoadingLogs(false);
      }
    };

    if (!loadingOwnerID) {
      fetchIntegrationsLinksAndLogs();
    }
  }, [courseID, ownerID, loadingOwnerID]);

  const StyledTextField = styled((props: TextFieldProps) => <TextField {...props} />)(({ theme }) => ({
    '& .MuiInputBase-root': {
      border: '0px solid red',
      backgroundColor: theme.palette.bgOne.main,
      borderRadius: theme.shape.borderRadius,
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&:focus fieldset': {
        borderColor: 'transparent',
      },
      '&:active fieldset': {
        borderColor: 'transparent',
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
      },
    },
  }));

  return (
    <>
      <ViewWrapper title={'Publish'}>
        <div style={{ display: 'flex', gap: theme.spacing(3), flex: '1 1 auto', flexWrap: 'nowrap', flexDirection: breakpoint ? 'column' : 'row', overflow: breakpoint ? 'auto' : 'hidden' }}>
          <div
            style={{
              background: theme.palette.bgTwo.main,
              flex: breakpoint ? '1 1 auto' : '2 1 0',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: breakpoint ? '100%' : 'auto',
            }}
          >
            <Typography variant='body1' sx={{ fontWeight: 'bold', margin: theme.spacing(2) }}>
              Publish Settings
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
              <div className={classes.setting} style={{ flexDirection: 'row' }}>
                <Typography variant='body1'>Language</Typography>
                <Select variant={'standard'} size={'small'} value={language} onChange={(e) => setLanguage(e.target.value)} sx={{ minWidth: 100, maxWidth: 160 }}>
                  <MenuItem value={'en'}>English</MenuItem>
                  <MenuItem value={'no'}>Norwegian</MenuItem>
                  <MenuItem value={'se'}>Swedish</MenuItem>
                  <MenuItem value={'fi'}>Finnish</MenuItem>
                  <MenuItem value={'es'}>Spanish</MenuItem>
                  <MenuItem value={'de'}>German</MenuItem>
                  <MenuItem value={'fr'}>French</MenuItem>
                </Select>
              </div>
              <Divider />
              {courseProperties && (
                <>
                  <div className={classes.setting} style={{ flexDirection: 'row' }}>
                    <Typography variant='body1'>Logo</Typography>
                    <DynamicImageField property={courseProperties.settings.items.companyLogo} imgSize={{ width: 200, height: 40 }} />
                  </div>
                  <Divider />
                </>
              )}
              {/* Linked Integrations Section */}
              <div className={classes.setting} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: theme.spacing(1) }}>
                  <Typography variant='body1'>Linked Integrations</Typography>
                  <Button size='small' startIcon={<AddIcon />} onClick={() => setShowIntegrationsModal(true)} disabled={loadingOwnerID || loadingIntegrations || !ownerID || integrations.length === 0}>
                    Add Integration
                  </Button>
                </div>

                <div style={{ width: '100%' }}>
                  {linkedIntegrations.map((integration, index) => (
                    <div key={`${integration.customerId}_${integration.courseLMSLink?.lmsCustomerCourseId}_${index}`}>
                      <LinkedIntegrations
                        integration={integration}
                        publishingStates={publishingStates}
                        handlePublishToIntegration={() => handlePublishToIntegration(integration)}
                        handleRemoveIntegration={() => handleRemoveIntegration(integration.customerId)}
                        handleOpenIntegrationSettings={() => handleOpenIntegrationSettings(integration)}
                      />
                    </div>
                  ))}
                </div>
                <Dialog
                  open={integrationSettings}
                  onClose={handleCloseIntegrationSettings}
                  maxWidth='sm'
                  PaperProps={{
                    sx: { background: theme.palette.bgTwo.main, color: theme.palette.bgTwo.contrastText },
                  }}
                >
                  <DialogTitle>Choose Course</DialogTitle>
                  <DialogContent>
                    <DialogContentText>Choose which course in your connected LMS you want to update, or to create a new course in the LMS.</DialogContentText>
                    {!isTargetCourseListLoaded ? (
                      <CircularLoading loadingText='Loading course list...' />
                    ) : (
                      <>
                        <FormControl sx={{ minWidth: 240, margin: '32px 0' }}>
                          <InputLabel id='targetCourseLabel'>Target Course</InputLabel>
                          <Select
                            labelId='targetCourseLabel'
                            id='targetCourse'
                            value={targetCourse}
                            label='Target Course'
                            onChange={handleChangeTargetCourse}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                            }}
                          >
                            <MenuItem value={0}>New Course</MenuItem>
                            {targetCourseList.courseList.map((course, index) => (
                              <MenuItem key={`${course.CourseId}_${index}`} value={Number(course.CourseId)}>
                                {course.Name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <DialogContentText>
                          {targetCourse == 0
                            ? 'The first time you publish, a brand-new course will be created in the LMS. Any later publishes will update that same course automatically.'
                            : 'The publish will update that existing course instead, and all future publishes will continue updating it.'}
                        </DialogContentText>
                      </>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => handleUpdateIntegration()}>Select</Button>
                    <Button onClick={() => handleCloseIntegrationSettings()}>Cancel</Button>
                  </DialogActions>
                </Dialog>
              </div>
              {loadingIntegrations ? (
                <>
                  <CircularLoading loadingText='Loading Integrations...' />
                </>
              ) : (
                <>
                  <div style={{ flex: '1 1 auto' }}>{/* Filler div - Bare for å dytte knappen ned. */}</div>

                  <Button
                    size='small'
                    color='primary'
                    variant='contained'
                    style={{ margin: theme.spacing(2), alignSelf: 'flex-start' }}
                    onClick={handlePublishToAllIntegrations}
                    startIcon={<PublishIcon fontSize='small' />}
                    sx={{ padding: '4px 10px', minHeight: 28, fontSize: '0.8125rem' }}
                    disabled={Object.values(publishingStates).some((isPublishing) => isPublishing) || linkedIntegrations.length === 0}
                  >
                    Publish All
                  </Button>
                </>
              )}
            </div>
          </div>

          <div
            style={{
              background: theme.palette.bgTwo.main,
              flex: breakpoint ? '1 1 auto' : '3 1 0',
              maxHeight: breakpoint ? 'unset' : '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: breakpoint ? '100%' : 'auto',
            }}
          >
            <div style={{ padding: theme.spacing(2), display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                Publish History
              </Typography>

              <StyledTextField
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.palette.text.primary }}>
                    <SearchIcon />
                    Search
                  </div>
                }
                variant='outlined'
              />
            </div>

            <div style={{ flex: '1 1 auto', background: theme.palette.bgTwo.main, display: 'flex', flexDirection: 'column', overflow: breakpoint ? 'auto' : 'auto' }}>
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 0.5fr 0.5fr 0.75fr',
                    gap: 16,
                    padding: '24px 16px',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.bgSix.main,
                    borderBottom: '1px solid #515151',
                    zIndex: 999,
                    fontWeight: 'bold',
                  }}
                >
                  <div>LMS Course</div>
                  <div>Status</div>
                  <div>Date Published</div>
                  <div>Tools</div>
                </div>
                <div>
                  {publishLogs && (
                    <>
                      <TransitionGroup>
                        {publishLogs.map((log) => (
                          <Collapse key={`${log.publishLog.publishDate}`} className={classes.publishRow} timeout={transitionDuration}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.5fr 0.5fr 0.75fr', gap: 16, padding: 16, alignItems: 'center' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 8, minWidth: 0 }}>
                                <Typography variant='body2' style={{ fontWeight: 500 }}>
                                  {log.integrationUrl}
                                </Typography>
                                <Tooltip title={log.publishLog.lmsCourseName} arrow>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    noWrap
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      wordBreak: 'break-word',
                                      maxWidth: 220,
                                    }}
                                  >
                                    {log.publishLog.lmsCourseName}
                                  </Typography>
                                </Tooltip>
                              </div>
                              <div style={{ color: getStatusColor(log.message), fontWeight: 500 }}>{getStatusText(log.message)}</div>
                              <div>{moment.utc(log.upload_date).local().format('DD.MM.YYYY HH:mm')}</div>
                              <div style={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
                                {log.permalink && log.permalink !== 'Unknown Integration' && log.publishLog.lmsTypeId !== 2 && (
                                  <IconButton size='small' onClick={() => window.open(log.permalink, '_blank')} title='Open in new tab' style={{ padding: 4 }}>
                                    <OpenInNewIcon fontSize='small' />
                                  </IconButton>
                                )}
                              </div>
                            </div>
                          </Collapse>
                        ))}
                      </TransitionGroup>
                    </>
                  )}
                </div>
              </div>
              {publishLogs === undefined ||
                (publishLogs.length <= 0 && (
                  <>
                    <div
                      style={{
                        flex: '1 1 auto',
                        background: theme.palette.bgOne.main,
                        width: '100%',
                        display: 'grid',
                        color: '#707070',
                        alignContent: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      {loadingLogs ? (
                        <div style={{ color: '#EEE' }}>
                          <CircularLoading loadingText='Loading Publish Logs...' />
                        </div>
                      ) : (
                        <>
                          <span>You haven&apos;t published anything yet.</span>
                          <span>When you do we will display your publish history here.</span>
                          <span style={{ marginTop: 16 }}>(ﾉ&gt;ω&lt;)ﾉ :｡･:*:･ﾟ&apos;★,｡･:*:･ﾟ&apos;☆</span>
                        </>
                      )}
                    </div>
                  </>
                ))}
              <div style={{ flex: '1 1 auto', background: theme.palette.bgOne.main }}></div>
            </div>
          </div>
        </div>

        {/* Integration Selection Modal */}
        <Dialog
          open={showIntegrationsModal}
          onClose={() => setShowIntegrationsModal(false)}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: { background: theme.palette.bgTwo.main, color: theme.palette.bgTwo.contrastText },
          }}
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LinkIcon />
              Add Integration Link
            </div>
          </DialogTitle>
          <DialogContent>
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: theme.spacing(2) }}>
              Select an integration to link from your connections. When you publish, it will be automatically sent to the linked integration.
            </Typography>

            {loadingOwnerID || loadingIntegrations ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(3) }}>
                <CircularProgress />
              </div>
            ) : !ownerID ? (
              <Typography color='error'>Failed to load user information</Typography>
            ) : integrations.length === 0 ? (
              <Typography color='textSecondary'>No integrations available. Please add integrations in the Integrations section first.</Typography>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                {integrations.filter(isSelectableIntegration).map((integration) => (
                  <ListItem key={integration.customerId} disablePadding style={{ backgroundColor: theme.palette.bgThree.main, borderRadius: theme.shape.borderRadius }}>
                    <ListItemButton onClick={() => handleAddIntegration(integration)} disabled={linkingIntegration === integration.customerId}>
                      <ListItemIcon>
                        {linkingIntegration === integration.customerId ? (
                          <CircularProgress size={36} />
                        ) : (
                          <div style={{ height: 34, width: 34 }}>
                            {integration.lmsTypeId === 1 && <WPLogoIcon />}
                            {integration.lmsTypeId === 2 && <NiasLogoIcon />}
                          </div>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={integration.url}
                        secondary={
                          linkingIntegration === integration.customerId ? (
                            'Linking...'
                          ) : (
                            <>
                              {integration.lmsTypeId === 1 && 'WordPress Integration'}
                              {integration.lmsTypeId === 2 && 'Mentorkit Integration'}
                            </>
                          )
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {integrations.filter(isSelectableIntegration).length === 0 && (
                  <Typography color='textSecondary' style={{ padding: theme.spacing(2), textAlign: 'center' }}>
                    All available integrations are already linked.
                  </Typography>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowIntegrationsModal(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </ViewWrapper>
    </>
  );
});
